// client/src/services/adminInbox.ts
// Client for the Admin Team Inbox Triage lane on the Desks board. Emails sent to
// AdminTeam@bmss.com (pulled into Feedback.BugReports by func-shared-services as
// ContextApp='admin-inbox') show as cards; reads hit admin-hq direct-SQL, writes
// (assign / mark handled) hop through func-shared-services (the sole writer).

// The lane an email sits in (Feedback.BugReports.Status). resolved = Completed.
export type InboxStatus = 'inbox_triage' | 'in_progress' | 'resolved';

function normStatus(s: string): InboxStatus {
  if (s === 'in_progress') return 'in_progress';
  if (s === 'resolved' || s === 'wont_fix') return 'resolved';
  return 'inbox_triage';
}

// A file attachment captured with an inbox email — just the ref; download goes through
// /api/desks-inbox?mode=attachment (same blob relay the Dev Team board uses).
export interface AttachmentRef {
  id: string;
  name: string;
  contentType: string | null;
  size: number | null;
}

export interface InboxItem {
  bugReportId: number;
  status: InboxStatus;
  from: string;               // sender (SubmittedBy)
  subject: string;            // Title
  preview: string;            // cleaned body (Happened)
  receivedAt: string | null;  // CreatedAt (ISO)
  assignedTo: string | null;  // email of the admin it's assigned to, or null
  webLink: string | null;     // Outlook deep link (mailbox-scoped; not shown — only works with access to that mailbox)
  threadCount: number;        // rolled-up replies in the conversation
  attachments: AttachmentRef[]; // file attachments captured at intake (bytes live in blob)
  important: boolean;         // moved to the Important Reading column
  readCount: number;          // how many admins have marked it read
  hasRead: boolean;           // has the CURRENT admin marked it read
  notes: string;              // board notes ('' if none)
  notesUpdatedBy: string;     // email of who last edited the notes
  notesUpdatedAt: string;     // ISO timestamp of the last notes edit
}

export interface InboxPerson {
  name: string;
  email: string;
}

export interface InboxReader {
  email: string;
  readAt: string | null;
}

function mapItem(row: any): InboxItem {
  return {
    bugReportId: Number(row.bugReportId),
    status:      normStatus(row.status),
    from:        row.submittedBy || '(unknown sender)',
    subject:     row.title || '(no subject)',
    preview:     row.happened || '',
    receivedAt:  row.createdAt || null,
    assignedTo:  row.assignedTo || null,
    webLink:     row.webLink || null,
    threadCount: Number(row.threadCount) || 0,
    attachments: Array.isArray(row.attachments) ? row.attachments : [],
    important:   row.important === true,
    readCount:   Number(row.readCount) || 0,
    hasRead:     row.hasRead === true,
    notes:          row.notes || '',
    notesUpdatedBy: row.notesUpdatedBy || '',
    notesUpdatedAt: row.notesUpdatedAt || '',
  };
}

async function getJson(url: string): Promise<any> {
  const r = await fetch(url, { credentials: 'include', cache: 'no-store' });
  const b = await r.json().catch(() => ({}));
  if (!r.ok || b?.error) throw new Error(b?.error || `Server error ${r.status}`);
  return b;
}

async function postJson(url: string, body: unknown): Promise<any> {
  const r = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  const b = await r.json().catch(() => ({}));
  if (!r.ok || b?.error) throw new Error(b?.error || `Server error ${r.status}`);
  return b;
}

export async function fetchInbox(): Promise<InboxItem[]> {
  const d = await getJson('/api/desks-inbox?mode=list');
  return (d.items || []).map(mapItem);
}

export async function fetchInboxAssignees(): Promise<InboxPerson[]> {
  const d = await getJson('/api/desks-inbox?mode=assignees');
  return (d.people || []) as InboxPerson[];
}

// Assign an inbox email to an admin (by email). Pass '' to clear. Keeps its current lane.
export async function assignInboxItem(bugReportId: number, assignedTo: string, status: InboxStatus): Promise<void> {
  await postJson('/api/desks-inbox', { mode: 'assign', bugReportId, assignedTo, status });
}

// Move an inbox email to another lane (Inbox Triage / In Progress / Completed).
export async function setInboxStatus(bugReportId: number, status: InboxStatus): Promise<void> {
  await postJson('/api/desks-inbox', { mode: 'status', bugReportId, status });
}

// Email the original sender of an inbox email — sent AS the logged-in admin so
// replies come back to them (mirrors the Dev Team board).
// `to` / `cc` take comma- or semicolon-separated addresses so others can be added.
export async function emailInboxSender(input: { to: string; cc?: string; subject: string; body: string }): Promise<void> {
  await postJson('/api/desks-inbox', { mode: 'email', ...input });
}

// Move an inbox item to / from the Important Reading column.
export async function setInboxImportant(bugReportId: number, important: boolean): Promise<void> {
  await postJson('/api/desks-inbox', { mode: 'important', bugReportId, important });
}

// Mark an item read / unread for the current admin.
export async function setInboxRead(bugReportId: number, read: boolean): Promise<void> {
  await postJson('/api/desks-inbox', { mode: 'read', bugReportId, read });
}

// One rolled-up email reply in a thread (from ContextJson.thread[]).
export interface InboxThreadMessage {
  from: string | null;
  receivedAt: string | null;
  subject: string | null;
  body: string | null;
  messageId: string | null;
  attachments?: AttachmentRef[];
}

// The rolled-up email replies for one item (lazy-loaded when its thread button is
// clicked). Empty array if the item has no thread.
export async function fetchInboxThread(bugReportId: number): Promise<InboxThreadMessage[]> {
  const d = await getJson(`/api/desks-inbox?mode=thread&bugReportId=${encodeURIComponent(bugReportId)}`);
  return (d.thread || []) as InboxThreadMessage[];
}

// Browser-hittable download URL for one attachment (GET → streams the file).
export function inboxAttachmentUrl(bugReportId: number, attachmentId: string): string {
  return `/api/desks-inbox?mode=attachment&bugReportId=${encodeURIComponent(bugReportId)}&attachmentId=${encodeURIComponent(attachmentId)}`;
}

// Who has marked this item read (for the "who's read it" popover). Leadership only.
export async function fetchInboxReaders(bugReportId: number): Promise<InboxReader[]> {
  const d = await getJson(`/api/desks-inbox?mode=reads&bugReportId=${encodeURIComponent(bugReportId)}`);
  return (d.readers || []) as InboxReader[];
}
